import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, total } = useCart();
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  React.useEffect(() => {
    if (!open || items.length === 0) {
      setQuote(null);
      return;
    }
    let active = true;
    api
      .post("/checkout/quote", { items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })), origin_url: window.location.origin })
      .then(({ data }) => { if (active) setQuote(data); })
      .catch(() => {});
    return () => { active = false; };
  }, [open, items]);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[#F9F6F0] w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#262626]/10">
          <SheetTitle className="font-display font-black text-2xl text-[#262626] flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#D32F2F]" /> Your Cart
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-[#262626]/60">
            <ShoppingBag className="w-12 h-12 mb-4 text-[#262626]/20" />
            <p className="font-semibold">Your cart is empty</p>
            <p className="text-sm mt-1">Add some cards to get started.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {items.map((item) => (
              <div key={item.id} className="flex gap-3 bg-white rounded-lg border border-[#262626]/10 p-3" data-testid={`cart-item-${item.id}`}>
                <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-md bg-[#F9F6F0]" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-sm text-[#262626] line-clamp-2">{item.name}</p>
                  <p className="text-[#D32F2F] font-bold text-sm mt-1">${item.price.toFixed(2)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => updateQty(item.id, item.quantity - 1)} className="p-1 rounded border border-[#262626]/20 hover:bg-[#262626]/5" aria-label="Decrease">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="text-sm font-semibold w-6 text-center" data-testid={`cart-qty-${item.id}`}>{item.quantity}</span>
                    <button onClick={() => updateQty(item.id, item.quantity + 1)} className="p-1 rounded border border-[#262626]/20 hover:bg-[#262626]/5" aria-label="Increase">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <button onClick={() => removeItem(item.id)} className="text-[#262626]/40 hover:text-[#D32F2F] transition-colors self-start" data-testid={`cart-remove-${item.id}`} aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-[#262626]/10 px-6 py-5 bg-white">
            <div className="space-y-2 mb-4 text-sm">
              <div className="flex items-center justify-between text-[#262626]/80">
                <span>Subtotal</span>
                <span className="font-semibold" data-testid="cart-subtotal">${(quote ? quote.subtotal : total).toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-[#262626]/80">
                <span>Shipping</span>
                <span className="font-semibold" data-testid="cart-shipping">
                  {quote ? (quote.shipping === 0 ? "FREE" : `$${quote.shipping.toFixed(2)}`) : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between text-[#262626]/80">
                <span>Sales Tax (CA {quote ? (quote.tax_rate * 100).toFixed(2).replace(/\.?0+$/, "") : "8.25"}%)</span>
                <span className="font-semibold" data-testid="cart-tax">${(quote ? quote.tax : 0).toFixed(2)}</span>
              </div>
              {quote && quote.shipping > 0 && (
                <p className="text-xs text-[#D32F2F] pt-1" data-testid="free-shipping-hint">
                  Add ${(quote.free_shipping_threshold - quote.subtotal).toFixed(2)} more for FREE shipping!
                </p>
              )}
            </div>
            <div className="flex items-center justify-between mb-4 pt-3 border-t border-[#262626]/10">
              <span className="font-display font-bold text-[#262626]">Total</span>
              <span className="font-display font-black text-2xl text-[#262626]" data-testid="cart-total">
                ${(quote ? quote.total : total).toFixed(2)}
              </span>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              data-testid="checkout-button"
              className="w-full bg-[#D32F2F] text-white font-bold py-3.5 rounded-md hover:bg-[#B71C1C] transition-colors disabled:opacity-60"
            >
              {loading ? "Redirecting…" : "Checkout"}
            </button>
            <p className="text-xs text-center text-[#262626]/50 mt-3">Secure checkout powered by Stripe · Ships from Galt, CA</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
